import { GuestFlightScheduleLiteModel } from 'api-hooks/cro/model';
import { StatefulTooltip } from 'baseui/tooltip';
import { flightCheckInOptions } from 'components/elements/table/editable-columns/check-in-options';
import {
  ColumnDataType,
  IColumn as EditableIColumn,
} from 'components/elements/table/type';
import Text from 'components/elements/text';
import useDate from 'hooks/use-date';
import useTranslation from 'next-translate/useTranslation';

import CheckInSelect from '../components/check-in-select';
import FlightScheduleCheckboxHeader from '../components/flight-schedule/flight-schedule-checkbox-header';

export default function useFlightScheduleTableList(
  type: 'default' | 'editable' = 'default',
) {
  const { t } = useTranslation();
  const formatDate = useDate();
  const isEditable = type === 'editable';
  const checkBoxColumn: EditableIColumn<GuestFlightScheduleLiteModel>[] =
    isEditable
      ? [
          {
            dataType: ColumnDataType.checkbox,
            accessorKey: 'isCheck',
            header: () => <FlightScheduleCheckboxHeader />,
            maxSize: 42,
            minSize: 42,
            stickyLeft: true,
          },
        ]
      : [];

  return [
    ...checkBoxColumn,
    {
      dataType: ColumnDataType.select,
      accessorKey: 'checkInStatus',
      header: t('common:status'),
      minSize: '200px',
      maxSize: '200px',
      cell: isEditable
        ? undefined
        : ({ row }) => {
            return (
              <CheckInSelect
                type="flight"
                key={row.original.id}
                original={row.original}
              />
            );
          },
      options: flightCheckInOptions(t),
    },
    {
      dataType: ColumnDataType.null,
      accessorKey: 'date',
      header: t('common:date'),
      sortName: 'date',
      maxSize: 120,
      minSize: 120,
      cell: ({ row }) => {
        const date = row.original.date;
        return date ? formatDate(date, 'dd MMM yyyy') : '-';
      },
    },
    {
      dataType: ColumnDataType.null,
      accessorKey: 'customer',
      header: t('common:customer'),
      minSize: 180,
      cell: ({ row }) => {
        return row.original.customer.name;
      },
    },
    {
      dataType: ColumnDataType.null,
      accessorKey: 'guests',
      header: t('common:guest'),
      maxSize: 120,
      minSize: 120,
      cell({ row }) {
        const preview = row.original.guests.slice(0, 5);
        const more = row.original.guests.slice(5);

        const guestComponennts = (isMore: boolean = true) => (
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            {(isMore ? more : preview).map((guest) => {
              return (
                <Text variant="ParagraphSmall" key={guest.id}>
                  {guest.name}
                </Text>
              );
            })}
          </div>
        );

        return (
          <>
            {guestComponennts(false)}
            {more.length !== 0 && (
              <StatefulTooltip
                content={() => guestComponennts()}
                overrides={{
                  Body: {
                    style: {
                      zIndex: 10,
                    },
                  },
                  Inner: {
                    style: {
                      backgroundColor: 'white',
                    },
                  },
                }}
              >
                {t('common:more')}
              </StatefulTooltip>
            )}
          </>
        );
      },
    },
    {
      dataType: ColumnDataType.null,
      accessorKey: 'airline',
      header: t('common:airline'),
      maxSize: 180,
      minSize: 180,
      cell: ({ row }) => {
        const airline = row.original.airline;
        return `${airline.code} - ${airline.name}`;
      },
    },
    {
      dataType: ColumnDataType.null,
      accessorKey: 'route',
      header: t('common:route'),
      maxSize: 180,
      minSize: 180,
      cell: ({ row }) => {
        const route = row.original.route;
        return route;
      },
    },
    {
      dataType: ColumnDataType.null,
      accessorKey: 'time',
      header: t('common:time'),
      maxSize: 100,
      minSize: 100,
      cell: ({ row }) => {
        const time = row.original.time;
        return time;
      },
    },
    {
      dataType: ColumnDataType.null,
      accessorKey: 'bookingCode',
      header: t('common:booking_code'),
      maxSize: 180,
      minSize: 180,
      cell: ({ row }) => {
        const bookingCode = row.original.bookingCode;
        return bookingCode;
      },
    },
    {
      dataType: ColumnDataType.null,
      accessorKey: 'operator',
      header: t('common:operator'),
      maxSize: 180,
      minSize: 180,
      cell: ({ row }) => {
        const operator = row.original.operator?.name;
        return operator;
      },
    },
    {
      dataType: ColumnDataType.null,
      accessorKey: 'sales',
      header: t('common:sales'),
      maxSize: 180,
      minSize: 180,
      cell: ({ row }) => {
        const sales = row.original.sales?.name;
        return sales;
      },
    },
    {
      dataType: ColumnDataType.null,
      accessorKey: 'lead',
      header: t('invoice:lead'),
      maxSize: 180,
      minSize: 180,
      cell: ({ row }) => {
        const lead = row.original.lead?.name;
        return lead;
      },
    },
    {
      dataType: ColumnDataType.null,
      accessorKey: 'invoiceNumber',
      header: t('common:invoice_number'),
      maxSize: 180,
      minSize: 180,
      cell: ({ row }) => {
        const invoiceNumber = row.original.invoiceNumber;
        return invoiceNumber;
      },
    },
  ] as any;
}
